import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import axios from 'axios';
import moment from 'moment';
import Papa from 'papaparse';
import { Link } from 'react-router';
import {
  Button,
  Card,
  Icon,
  Label,
  Message,
  Statistic,
  Table,
} from 'semantic-ui-react';

import { authService } from '../../utils/auth-service';

const POLL_INTERVAL_MS = 10000;
// listSends in dyzio-email-sender returns at most this many sends.
const MAX_SENDS = 20;

// "Today" / "Yesterday" / "May 6" / "May 6, 2025" — same logic Slack/Linear use.
const dayLabelFor = unixSeconds => {
  const m = moment.unix(unixSeconds);
  const today = moment().startOf('day');
  const yesterday = moment().subtract(1, 'day').startOf('day');
  const sendDay = m.clone().startOf('day');
  if (sendDay.isSame(today)) return 'Today';
  if (sendDay.isSame(yesterday)) return 'Yesterday';
  if (m.year() === moment().year()) return m.format('MMM D');
  return m.format('MMM D, YYYY');
};

// Backend returns sends newest-first; preserve that ordering within groups.
const groupSendsByDay = sends => {
  const groups = [];
  sends.forEach(s => {
    const label = dayLabelFor(s.createdAt);
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.sends.push(s);
    } else {
      groups.push({ label, sends: [s] });
    }
  });
  return groups;
};

const OUTCOMES = [
  { key: 'sent', label: 'sent', color: '#2c8a4a', hint: 'Accepted by Mandrill for delivery' },
  { key: 'failed', label: 'failed', color: '#b94232', hint: 'Rejected by Mandrill' },
  { key: 'skipped', label: 'skipped', color: '#9e9e9e', hint: 'Missing or invalid email address' },
  { key: 'pending', label: 'waiting', color: '#1f6dbf', hint: 'Not sent yet' },
];

const STATUS = {
  COMPLETE: { text: 'Sent', color: 'green', accent: '#2c8a4a' },
  PARTIAL: { text: 'Partly sent', color: 'orange', accent: '#e8a317' },
  FAILED: { text: 'Failed', color: 'red', accent: '#b94232' },
  PROCESSING: { text: 'Sending', color: 'blue', accent: '#1f6dbf' },
};

const RECIPIENT_STATUS = {
  SENT: { text: 'Sent', color: 'green' },
  FAILED: { text: 'Failed', color: 'red' },
  SKIPPED: { text: 'Skipped', color: 'grey' },
  PENDING: { text: 'Waiting', color: 'blue' },
  SENDING: { text: 'Sending', color: 'blue' },
};

const DETAIL_LABEL_STYLE = { color: '#888', width: 80, flexShrink: 0 };

const Detail = ({ label, value }) => (
  <div style={{ display: 'flex', fontSize: 13, lineHeight: 1.8 }}>
    <span style={DETAIL_LABEL_STYLE}>{label}</span>
    <span style={{ color: '#333', wordBreak: 'break-all' }}>{value}</span>
  </div>
);

Detail.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

const OutcomeBar = ({ counts }) => (
  <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: '#eee' }}>
    {OUTCOMES.map(o => (
      counts[o.key] > 0 && (
        <div
          key={o.key}
          title={`${counts[o.key]} ${o.label}`}
          style={{ width: `${(counts[o.key] / counts.total) * 100}%`, background: o.color }}
        />
      )
    ))}
  </div>
);

OutcomeBar.propTypes = { counts: PropTypes.object.isRequired };

const DayHeading = ({ label }) => (
  <div
    style={{
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: 0.5,
      textTransform: 'uppercase',
      color: '#888',
      margin: '20px 0 8px',
    }}
  >
    {label}
  </div>
);

DayHeading.propTypes = { label: PropTypes.string.isRequired };

class CampaignEmailHistory extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sends: null,
      loading: true,
      refreshing: false,
      error: null,
      expanded: {},
      // sendId -> { loading, error, recipients }
      recipientsBySend: {},
      downloading: false,
      downloadError: null,
    };
    this._mounted = false;
    this._pollTimer = null;
  }

  componentDidMount() {
    this._mounted = true;
    this.fetch({ isRefresh: false });
  }

  componentWillUnmount() {
    this._mounted = false;
    this.clearPoll();
  }

  clearPoll = () => {
    if (this._pollTimer) {
      clearInterval(this._pollTimer);
      this._pollTimer = null;
    }
  }

  authHeaders = () => {
    const token = authService.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  setRecipients = (sendId, value) => this.setState(prev => ({
    recipientsBySend: { ...prev.recipientsBySend, [sendId]: value },
  }))

  requestRecipients = async sendId => {
    const url = process.env.REACT_APP_AD_HOC_EMAIL_URL;
    const response = await axios.get(`${url}/campaigns/${this.props.params.id}/sends/${sendId}/recipients`, {
      timeout: 15000,
      headers: this.authHeaders(),
    });
    return _.get(response, 'data.data.recipients', []);
  }

  fetchRecipients = async sendId => {
    this.setRecipients(sendId, { ...this.state.recipientsBySend[sendId], loading: true, error: null });
    try {
      const recipients = await this.requestRecipients(sendId);
      if (!this._mounted) return;
      this.setRecipients(sendId, { loading: false, error: null, recipients });
    } catch (err) {
      if (!this._mounted) return;
      const message = _.get(err, 'response.data.error') || err.message || 'Could not load recipients.';
      this.setRecipients(sendId, { loading: false, error: message, recipients: null });
    }
  }

  toggleRecipients = sendId => () => {
    const isOpen = !!this.state.expanded[sendId];
    this.setState(prev => ({ expanded: { ...prev.expanded, [sendId]: !isOpen } }));
    if (!isOpen && !_.get(this.state.recipientsBySend, [sendId, 'recipients'])) {
      this.fetchRecipients(sendId);
    }
  }

  // One row per recipient of every listed send, fetched on click so the page itself stays light.
  downloadCsv = async () => {
    const { sends } = this.state;
    this.setState({ downloading: true, downloadError: null });
    try {
      const perSend = await Promise.all(sends.map(send => this.requestRecipients(send.sendId)));
      const rows = _.flatMap(sends, (send, i) => perSend[i].map(r => ({
        'Sent at': send.createdAt ? moment.unix(send.createdAt).format('YYYY-MM-DD HH:mm') : '',
        Subject: send.subject || '',
        Template: send.mandrillTemplate || '',
        'Sent by': send.senderName || send.senderEmail || '',
        From: send.fromEmail ? `${send.fromName || ''} <${send.fromEmail}>`.trim() : '',
        Recipient: r.name,
        Email: r.email,
        Status: r.status,
        Reason: r.failureReason || '',
        'Discount code': r.discountCode || '',
        'Workflow status set': send.newWorkflowStatus && r.workflowStatusSet ? send.newWorkflowStatus : '',
      })));
      const campaignName = _.invoke(this.props.campaign, 'get', 'name') || 'campaign';
      // The BOM makes Excel read the file as UTF-8, so dashes and emoji in subjects survive.
      const blob = new Blob([`\ufeff${Papa.unparse(rows)}`], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${_.kebabCase(campaignName)}-email-history-${moment().format('YYYY-MM-DD')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      if (this._mounted) this.setState({ downloading: false });
    } catch (err) {
      if (this._mounted) {
        this.setState({ downloading: false, downloadError: err.message || 'Could not build the CSV.' });
      }
    }
  }

  goToInfluencers = () => this.context.router.push(`/campaign/${this.props.params.id}/collaborations`)

  fetch = async ({ isRefresh = false } = {}) => {
    if (this._mounted) {
      this.setState(isRefresh ? { refreshing: true } : { loading: true });
    }

    const url = process.env.REACT_APP_AD_HOC_EMAIL_URL;
    const campaignId = this.props.params.id;

    if (!url) {
      this.setState({
        loading: false,
        refreshing: false,
        error: 'REACT_APP_AD_HOC_EMAIL_URL is not configured.',
      });
      return;
    }

    try {
      const response = await axios.get(`${url}/campaigns/${campaignId}/sends`, {
        timeout: 15000,
        headers: this.authHeaders(),
      });
      // Test sends are excluded from this view — they're noise when the user
      // is reviewing what real recipients received.
      const sends = _.get(response, 'data.data.sends', []).filter(s => !s.isTest);
      if (!this._mounted) return;
      this.setState({ sends, loading: false, refreshing: false, error: null });
      // Open recipient lists follow the refresh, so a send in progress fills in.
      _.keys(_.pickBy(this.state.expanded)).forEach(this.fetchRecipients);

      // Auto-poll while anything is still in flight.
      const stillProcessing = sends.some(s => s.status === 'PROCESSING');
      if (stillProcessing) {
        if (!this._pollTimer) {
          this._pollTimer = setInterval(() => this.fetch({ isRefresh: true }), POLL_INTERVAL_MS);
        }
      } else {
        this.clearPoll();
      }
    } catch (err) {
      if (!this._mounted) return;
      const message = _.get(err, 'response.data.error') || err.message || 'Could not load history.';
      this.setState({ loading: false, refreshing: false, error: message });
    }
  }

  renderSummary = sends => {
    const totals = _.reduce(sends, (acc, s) => _.mergeWith(acc, s.counts, _.add), {});
    const stats = [
      { label: 'Sends', value: sends.length },
      { label: 'Emails sent', value: totals.sent || 0 },
      { label: 'Failed', value: totals.failed || 0 },
      { label: 'Skipped', value: totals.skipped || 0 },
    ];
    return (
      <Statistic.Group size="tiny" widths={stats.length}>
        {stats.map(s => (
          <Statistic key={s.label}>
            <Statistic.Value>{s.value}</Statistic.Value>
            <Statistic.Label>{s.label}</Statistic.Label>
          </Statistic>
        ))}
      </Statistic.Group>
    );
  }

  renderRecipients = (sendId, newWorkflowStatus) => {
    const { loading, error, recipients } = this.state.recipientsBySend[sendId] || {};
    const campaignId = this.props.params.id;

    if (loading && !recipients) {
      return (
        <div style={{ padding: '16px 0', color: '#888', fontSize: 13 }}>
          <Icon loading name="spinner" /> Loading recipients…
        </div>
      );
    }
    if (error) {
      return (
        <Message negative size="small">
          <Icon name="exclamation circle" />
          {error}
        </Message>
      );
    }
    if (!recipients) return null;

    return (
      <div style={{ maxHeight: 420, overflowY: 'auto', marginTop: 8 }}>
        <Table compact basic="very" size="small" unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Recipient</Table.HeaderCell>
              <Table.HeaderCell>Email</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Details</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {recipients.map((r, i) => {
              const statusInfo = RECIPIENT_STATUS[r.status] || { text: r.status, color: 'grey' };
              const detail = _.compact([
                r.failureReason,
                r.discountCode && `Code ${r.discountCode}`,
                r.workflowStatusSet === true && `Status set to ${newWorkflowStatus}`,
              ]).join(' · ');
              return (
                <Table.Row key={`${r.email}-${i}`} negative={r.status === 'FAILED'}>
                  <Table.Cell>
                    {r.discussionId
                      ? <Link to={`/campaign/${campaignId}/collaboration/${r.discussionId}`}>{r.name || '—'}</Link>
                      : (r.name || '—')}
                  </Table.Cell>
                  <Table.Cell style={{ wordBreak: 'break-all' }}>
                    {r.email || <span style={{ color: '#aaa' }}>no email</span>}
                  </Table.Cell>
                  <Table.Cell>
                    <Label basic color={statusInfo.color} size="tiny">{statusInfo.text}</Label>
                  </Table.Cell>
                  <Table.Cell style={{ color: '#777' }}>
                    {detail || (r.workflowStatusSet === false ? null : '—')}
                    {r.workflowStatusSet === false && (
                      <span style={{ color: '#b94232' }}>
                        <Icon name="warning sign" />Status not set
                      </span>
                    )}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </div>
    );
  }

  renderSend = send => {
    const {
      sendId, createdAt, senderName, senderEmail, subject, counts, status,
      fromName, fromEmail, replyTo, mandrillTemplate, newWorkflowStatus,
    } = send;
    const statusInfo = STATUS[status] || { text: status, color: 'grey', accent: '#ddd' };
    const sentAt = createdAt ? moment.unix(createdAt) : null;
    const fromLabel = fromName && fromEmail ? `${fromName} <${fromEmail}>` : (fromName || fromEmail);
    const isExpanded = !!this.state.expanded[sendId];

    return (
      <div
        key={sendId}
        style={{
          border: '1px solid #e6e6e6',
          borderLeft: `4px solid ${statusInfo.accent}`,
          borderRadius: 4,
          padding: '16px 20px',
          marginBottom: 12,
          background: '#fff',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ minWidth: 0, marginRight: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#1a1a1a' }}>
              {subject || <span style={{ color: '#aaa', fontWeight: 'normal' }}>(no subject)</span>}
            </div>
            <div style={{ fontSize: 13, color: '#777', marginTop: 4 }}>
              {sentAt ? `${sentAt.format('MMM D, YYYY [at] h:mm A')} (${sentAt.fromNow()})` : '—'}
              {' · by '}{senderName || senderEmail || 'Unknown'}
            </div>
          </div>
          <Label color={statusInfo.color} size="small" style={{ flexShrink: 0 }}>
            {status === 'PROCESSING' && <Icon loading name="spinner" />}
            {statusInfo.text}
          </Label>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', marginTop: 14 }}>
          <div style={{ flex: '1 1 320px', marginRight: 24 }}>
            {fromLabel && <Detail label="From" value={fromLabel} />}
            {replyTo && <Detail label="Reply-to" value={replyTo} />}
            {mandrillTemplate && <Detail label="Template" value={mandrillTemplate} />}
            {newWorkflowStatus && <Detail label="Sets status" value={newWorkflowStatus} />}
          </div>
          <div style={{ flex: '1 1 280px', marginTop: 4 }}>
            <div style={{ fontSize: 13, color: '#333', marginBottom: 6 }}>
              <strong>{counts.sent}</strong> of <strong>{counts.total}</strong> recipient{counts.total === 1 ? '' : 's'} sent
            </div>
            <OutcomeBar counts={counts} />
            <div style={{ fontSize: 12, color: '#777', marginTop: 6 }}>
              {OUTCOMES.filter(o => o.key !== 'sent' && counts[o.key] > 0).map(o => (
                <span key={o.key} title={o.hint} style={{ marginRight: 12 }}>
                  <Icon name="circle" style={{ color: o.color }} />
                  {counts[o.key]} {o.label} — {o.hint.toLowerCase()}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 14, paddingTop: 10 }}>
          <Button basic size="tiny" onClick={this.toggleRecipients(sendId)}>
            <Icon name={isExpanded ? 'chevron up' : 'chevron down'} />
            {isExpanded ? 'Hide recipients' : `Show ${counts.total} recipient${counts.total === 1 ? '' : 's'}`}
          </Button>
          {isExpanded && this.renderRecipients(sendId, newWorkflowStatus)}
        </div>
      </div>
    );
  }

  render() {
    const {
      sends, loading, refreshing, error, downloading, downloadError,
    } = this.state;
    const hasSends = !loading && !error && !_.isEmpty(sends);

    return (
      <div style={{ marginTop: 10 }}>
        <div className="breadcrumb-bar" style={{ borderBottom: 'none' }}>
          <img src="/icons/chevron-left.svg" alt="back to influencers" onClick={this.goToInfluencers} />
          <h3 onClick={this.goToInfluencers} style={{ cursor: 'pointer' }}>
            Back to influencers
          </h3>
        </div>

        <Card fluid>
          <Card.Content>
            <Button
              floated="right"
              size="tiny"
              basic
              icon
              labelPosition="left"
              loading={refreshing}
              disabled={loading || refreshing}
              onClick={() => this.fetch({ isRefresh: true })}
            >
              <Icon name="refresh" />
              Refresh
            </Button>
            <Button
              floated="right"
              size="tiny"
              basic
              icon
              labelPosition="left"
              loading={downloading}
              disabled={loading || downloading || _.isEmpty(sends)}
              onClick={this.downloadCsv}
            >
              <Icon name="download" />
              Download CSV
            </Button>
            <Card.Header>Email sending history</Card.Header>
            {downloadError && <div className="small" style={{ color: '#b94232', marginTop: 4 }}>{downloadError}</div>}
          </Card.Content>
          <Card.Content>
            <Card.Meta>
              Emails sent to this campaign&apos;s influencers from Quick Links, newest first.
              &quot;Sent&quot; means Mandrill accepted the email for delivery. Test sends are not listed.
            </Card.Meta>
          </Card.Content>

          {hasSends && <Card.Content>{this.renderSummary(sends)}</Card.Content>}

          <Card.Content>
            {loading && (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#888' }}>
                <Icon name="mail outline" size="huge" />
                <div style={{ marginTop: 12, fontSize: 14 }}>Loading sending history…</div>
              </div>
            )}

            {!loading && error && (
              <Message negative>
                <Icon name="exclamation circle" />
                {error}
              </Message>
            )}

            {!loading && !error && _.isEmpty(sends) && (
              <Message info>
                <Icon name="info circle" />
                No emails have been sent for this campaign yet.
              </Message>
            )}

            {hasSends && groupSendsByDay(sends).map(group => (
              <div key={group.label}>
                <DayHeading label={group.label} />
                {group.sends.map(this.renderSend)}
              </div>
            ))}

            {hasSends && sends.length >= MAX_SENDS && (
              <div className="small light" style={{ marginTop: 8 }}>
                Showing the {MAX_SENDS} most recent sends.
              </div>
            )}
          </Card.Content>
        </Card>
      </div>
    );
  }
}

CampaignEmailHistory.propTypes = {
  params: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
};

CampaignEmailHistory.contextTypes = {
  router: PropTypes.object.isRequired,
};

export default CampaignEmailHistory;



/** WEBPACK FOOTER **
 ** ./src/components/campaign-email-history/index.js
 **/